import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

// Escape user-generated content before injecting into print windows (XSS guard)
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
import { useNavigate } from 'react-router-dom';
import { Clock, Award, Calendar, User, Eye, Trash2, X, MessageCircle, Bot, Printer, EyeOff, CheckSquare, Square, XSquare, Target, Search, Lightbulb, Heart } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const History = ({ studentInfo }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [studentFilter, setStudentFilter] = useState('');
  const [evalRequests, setEvalRequests] = useState({});
  
  const isAdmin = studentInfo?.permissions?.admin === true;

  useEffect(() => {
    fetchSessions();
    fetchEvalRequests();
  }, []);

  const fetchEvalRequests = async () => {
    if (!studentInfo?.student_number) return;
    try {
      const resp = await axios.get(`${API}/evaluation-requests`, { params: { student_number: studentInfo.student_number } });
      const map = {};
      // Requests are sorted desc — keep only the most recent per (type, session)
      (resp.data || []).forEach(r => {
        const key = `${r.request_type}:${r.session_id}`;
        if (!map[key]) map[key] = r.status;
      });
      setEvalRequests(map);
    } catch {
      // silent
    }
  };
  
  const fetchSessions = async () => {
    try {
      const params = {};
      if (studentInfo?.student_number) {
        params.student_number = studentInfo.student_number;
      }
      if (isAdmin) {
        params.is_admin = true;
      }
      const response = await axios.get(`${API}/sessions`, { params });
      setSessions(response.data.sort((a, b) => new Date(b.start_time) - new Date(a.start_time)));
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const viewScenario = async (scenarioId) => {
    try {
      const response = await axios.get(`${API}/scenarios/${scenarioId}`);
      setSelectedScenario(response.data);
      setShowScenarioModal(true);
    } catch (error) {
      console.error('Error fetching scenario:', error);
      toast.error('Erreur lors du chargement du scénario');
    }
  };

  const viewConversation = (session) => {
    setSelectedSession(session);
    setShowConversationModal(true);
  };

  const printConversation = () => {
    if (!selectedSession) return;

    const printWindow = window.open('', '_blank');
    const transcript = selectedSession.transcript || [];
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Transcript de Conversation - ${esc(selectedSession.operator_name)}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
              color: #333;
            }
            .header {
              border-bottom: 3px solid #0ea5e9;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #0ea5e9;
              margin: 0 0 10px 0;
            }
            .session-info {
              background: #f0f9ff;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 30px;
            }
            .session-info p {
              margin: 5px 0;
            }
            .message {
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            .message-header {
              font-weight: bold;
              margin-bottom: 5px;
              color: #1e293b;
            }
            .operator-msg .message-header {
              color: #0ea5e9;
            }
            .citizen-msg .message-header {
              color: #f43f5e;
            }
            .message-content {
              padding: 12px 15px;
              border-radius: 8px;
              line-height: 1.6;
            }
            .operator-msg .message-content {
              background: #e0f2fe;
              border-left: 4px solid #0ea5e9;
            }
            .citizen-msg .message-content {
              background: #fef2f2;
              border-left: 4px solid #f43f5e;
            }
            .timestamp {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              text-align: center;
              color: #64748b;
              font-size: 14px;
            }
            @media print {
              body {
                margin: 20px;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📞 Transcript de Conversation 911</h1>
            <p>Simulateur de Formation des Répartiteurs</p>
          </div>

          <div class="session-info">
            <p><strong>Opérateur:</strong> ${esc(selectedSession.operator_name)}</p>
            <p><strong>Date:</strong> ${new Date(selectedSession.start_time).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p><strong>Durée:</strong> ${formatDuration(selectedSession.duration_seconds || 0)}</p>
            <p><strong>Nombre de messages:</strong> ${transcript.length}</p>
            <p><strong>Statut:</strong> ${getStatusLabel(selectedSession.status)}</p>
            <p style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #cbd5e1;">
              <strong>Consultation du scénario:</strong> 
              ${selectedSession.scenario_viewed 
                ? `<span style="color: #d97706;">✓ Consulté ${selectedSession.scenario_view_count || 1} fois</span>` 
                : `<span style="color: #059669;">✓ Non consulté - Travail autonome</span>`
              }
            </p>
          </div>

          <div class="conversation">
            ${transcript.map((message, index) => `
              <div class="message ${message.speaker === 'operator' ? 'operator-msg' : 'citizen-msg'}">
                <div class="message-header">
                  ${message.speaker === 'operator' ? '👤 OPÉRATEUR' : '🆘 CITOYEN'}
                </div>
                <div class="message-content">
                  ${esc(message.text)}
                </div>
                ${message.timestamp ? `
                  <div class="timestamp">
                    ${formatTimestamp(message.timestamp)}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>

          <div class="footer">
            <p>Document généré le ${new Date().toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p>Simulateur 911 - Formation Professionnelle</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const printHealthProtocol = (session) => {
    const hp = session.health_protocol;
    if (!hp) return;

    const priorityMap = {
      1: { label: 'PRIORITÉ 1', title: 'Arrêt cardiaque suspecté', color: '#DC2626', bg: '#FEE2E2' },
      2: { label: 'PRIORITÉ 2', title: 'Douleur thoracique critique', color: '#F59E0B', bg: '#FEF3C7' },
      4: { label: 'PRIORITÉ 4', title: 'Évaluation en cours / stable', color: '#22C55E', bg: '#DCFCE7' },
    };

    const p = priorityMap[hp.final_priority] || priorityMap[4];
    const rf = hp.red_flags || [];
    const transcript = session.transcript || [];
    const answers = hp.answers || {};
    const duration = session.duration_seconds || 0;
    const formatTime = function(s) { return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };

    // Helper to build a phase block
    const buildPhaseBlock = function(num, title, content) {
      return '<div style="margin-bottom:12px;padding:10px 12px;border:1px solid #e2e8f0;border-radius:8px;background:white;">'
        + '<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">'
        + '<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:#2E6CF5;color:white;font-size:11px;font-weight:bold;">' + num + '</span>'
        + '<span style="font-size:14px;font-weight:700;color:#002768;">' + title + '</span>'
        + '</div>' + content + '</div>';
    };

    // Helper for NEW structured format
    const buildPhaseHtml = function(num, title, phase) {
      let content = '';
      if (phase.type === 'info_gathering' && phase.fields) {
        Object.values(phase.fields).forEach(function(f) {
          content += '<div style="display:flex;gap:8px;padding:3px 0;border-bottom:1px solid #f1f5f9;">'
            + '<span style="font-weight:600;color:#374151;min-width:160px;font-size:13px;">' + f.label + ':</span>'
            + '<span style="color:#1e293b;font-size:13px;">' + (f.value || '—') + '</span></div>';
        });
      } else if (phase.type === 'open_question') {
        (phase.checked_items || []).forEach(function(item) {
          content += '<span style="display:inline-block;margin:2px 4px;padding:3px 10px;border-radius:12px;background:#DCFCE7;color:#166534;font-size:12px;font-weight:600;">&#10003; ' + item + '</span>';
        });
      } else if (phase.type === 'decision') {
        const val = String(phase.value || '');
        const isAlert = val.toLowerCase().includes('non') || val.toLowerCase().includes('impossible');
        const isUnknown = val.toLowerCase().includes('ne sais pas');
        if (phase.question) {
          content += '<div style="font-size:13px;color:#374151;margin-bottom:6px;font-style:italic;">"' + phase.question + '"</div>';
        }
        content += '<span style="display:inline-block;padding:4px 14px;border-radius:6px;font-size:13px;font-weight:600;background:' + (isAlert ? '#FEE2E2' : isUnknown ? '#FEF3C7' : '#DCFCE7') + ';color:' + (isAlert ? '#DC2626' : isUnknown ? '#92400E' : '#166534') + ';">' + val + '</span>';
      } else if (phase.type === 'multi_question' && phase.details) {
        Object.values(phase.details).forEach(function(d) {
          const ans = String(d.answer || '—');
          const isOui = ans === 'oui'; const isInconnue = ans === 'inconnue'; const isRF = d.red_flag && isOui;
          const bg = isRF ? '#FEE2E2' : isInconnue ? '#FEF3C7' : isOui ? '#DCFCE7' : '#F8FAFC';
          const tc = isRF ? '#DC2626' : isInconnue ? '#92400E' : isOui ? '#166534' : '#6B7280';
          const label = ans === 'oui' ? 'Oui' : ans === 'non' ? 'Non' : ans === 'inconnue' ? 'Inconnue' : ans;
          content += '<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid #f1f5f9;">'
            + '<span style="font-size:13px;color:#374151;">' + (isRF ? '&#9888; ' : '') + d.question + '</span>'
            + '<span style="padding:2px 10px;border-radius:6px;font-size:12px;font-weight:600;background:' + bg + ';color:' + tc + ';white-space:nowrap;">' + label + '</span></div>';
        });
      } else if (phase.type === 'comparison') {
        const val = String(phase.value || '');
        const isC = val.toLowerCase().includes('cardiaque');
        if (phase.question) {
          content += '<div style="font-size:13px;color:#374151;margin-bottom:6px;font-style:italic;">"' + phase.question + '"</div>';
        }
        content += '<span style="display:inline-block;padding:4px 14px;border-radius:6px;font-size:13px;font-weight:600;background:' + (isC ? '#FEE2E2' : '#FEF3C7') + ';color:' + (isC ? '#DC2626' : '#92400E') + ';">' + val + '</span>';
      }
      return buildPhaseBlock(num, title, content);
    };

    // Build structured phases HTML - supports both OLD format (flat keys) and NEW format (structured)
    const isNewFormat = Object.values(answers).some(function(v) { return typeof v === 'object' && v !== null && v.phase_number; });
    
    let phasesHtml = '';
    
    if (isNewFormat) {
      // NEW structured format
      const sortedPhases = Object.values(answers).sort(function(a, b) { return (a.phase_number || 0) - (b.phase_number || 0); });
      sortedPhases.forEach(function(phase) {
        phasesHtml += buildPhaseHtml(phase.phase_number, phase.phase_title, phase);
      });
    } else {
      // OLD flat format - reconstruct phase display
      // Phase 1: Info gathering fields
      const p1Fields = ['adresse', 'rappel', 'appelant', 'age', 'sexe'];
      const p1Labels = { adresse: 'Adresse exacte', rappel: 'Numéro de rappel', appelant: "Nom de l'appelant", age: 'Âge', sexe: 'Sexe' };
      let p1Content = '';
      let hasP1 = false;
      p1Fields.forEach(function(f) {
        if (answers[f]) {
          hasP1 = true;
          p1Content += '<div style="display:flex;gap:8px;padding:3px 0;border-bottom:1px solid #f1f5f9;">'
            + '<span style="font-weight:600;color:#374151;min-width:160px;font-size:13px;">' + (p1Labels[f] || f) + ':</span>'
            + '<span style="color:#1e293b;font-size:13px;">' + answers[f] + '</span></div>';
        }
      });
      if (hasP1) phasesHtml += buildPhaseBlock(1, "Contrôle initial de l'appel", p1Content);
      
      // Phase 2: Open question (stored as array)
      if (answers.phase_2 && Array.isArray(answers.phase_2)) {
        let c = '';
        answers.phase_2.forEach(function(item) {
          c += '<span style="display:inline-block;margin:2px 4px 2px 0;padding:3px 10px;border-radius:12px;background:#DCFCE7;color:#166534;font-size:12px;font-weight:600;">&#10003; ' + item + '</span>';
        });
        if (c) phasesHtml += buildPhaseBlock(2, 'Problème principal', c);
      }
      
      // Decision phases: 3, 4, 5, 8
      const decisionPhases = [
        { key: 'phase_3', num: 3, title: 'Évaluation de la conscience' },
        { key: 'phase_4', num: 4, title: 'Évaluation de la respiration' },
        { key: 'phase_5', num: 5, title: 'Évaluation de la parole' },
        { key: 'phase_8', num: 8, title: 'Évaluation de la détérioration' },
      ];
      decisionPhases.forEach(function(dp) {
        if (answers[dp.key] && typeof answers[dp.key] === 'string') {
          const val = answers[dp.key];
          const isAlert = val.toLowerCase().includes('non') || val.toLowerCase().includes('impossible') || val.toLowerCase().includes('agonique');
          const isUnknown = val.toLowerCase().includes('ne sais pas');
          const c = '<span style="display:inline-block;padding:4px 14px;border-radius:6px;font-size:13px;font-weight:600;'
            + 'background:' + (isAlert ? '#FEE2E2' : isUnknown ? '#FEF3C7' : '#DCFCE7') + ';'
            + 'color:' + (isAlert ? '#DC2626' : isUnknown ? '#92400E' : '#166534') + ';">'
            + val + '</span>';
          phasesHtml += buildPhaseBlock(dp.num, dp.title, c);
        }
      });
      
      // Multi-question phases: 6, 7, 9
      const multiPhases = [
        { key: 'phase_6', num: 6, title: 'Évaluation de la douleur thoracique', questions: {
          intensite: 'Intensité de la douleur (sur 10)',
          debut: 'La douleur est-elle apparue soudainement?',
          irradiation: 'La douleur se propage-t-elle vers le bras, la mâchoire ou le dos?'
        }, redFlags: ['irradiation'] },
        { key: 'phase_7', num: 7, title: 'Symptômes associés', questions: {
          difficulte_respiratoire: 'A-t-il de la difficulté à respirer?',
          transpiration: 'Transpire-t-il abondamment?',
          coloration: 'Devient-il pâle ou bleu?',
          nausees: 'A-t-il des étourdissements ou des nausées?'
        }, redFlags: ['difficulte_respiratoire', 'transpiration', 'coloration'] },
        { key: 'phase_9', num: 9, title: 'Antécédents', questions: {
          historique_cardiaque: 'A-t-il déjà eu un infarctus, de l\'angine ou des problèmes cardiaques?',
          nitro: 'A-t-il pris de la nitro récemment?',
          aspirine: 'A-t-il pris de l\'aspirine?',
          medicaments: 'Prend-il des médicaments cardiaques?'
        }, redFlags: [] },
      ];
      multiPhases.forEach(function(mp) {
        if (answers[mp.key] && typeof answers[mp.key] === 'object') {
          let c = '';
          Object.entries(mp.questions).forEach(function(entry) {
            const qId = entry[0];
            const qText = entry[1];
            const ans = answers[mp.key][qId];
            if (ans !== undefined) {
              const isOui = ans === 'oui';
              const isInconnue = ans === 'inconnue';
              const isRedFlag = mp.redFlags.includes(qId) && isOui;
              const bgColor = isRedFlag ? '#FEE2E2' : isInconnue ? '#FEF3C7' : isOui ? '#DCFCE7' : '#F8FAFC';
              const textColor = isRedFlag ? '#DC2626' : isInconnue ? '#92400E' : isOui ? '#166534' : '#6B7280';
              const ansLabel = ans === 'oui' ? 'Oui' : ans === 'non' ? 'Non' : ans === 'inconnue' ? 'Inconnue' : ans;
              c += '<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid #f1f5f9;">'
                + '<span style="font-size:13px;color:#374151;flex:1;">' + (isRedFlag ? '&#9888; ' : '') + qText + '</span>'
                + '<span style="padding:2px 10px;border-radius:6px;font-size:12px;font-weight:600;background:' + bgColor + ';color:' + textColor + ';margin-left:8px;white-space:nowrap;">' + ansLabel + '</span>'
                + '</div>';
            }
          });
          if (c) phasesHtml += buildPhaseBlock(mp.num, mp.title, c);
        }
      });
      
      // Phase 10: Comparison
      if (answers.phase_10 && typeof answers.phase_10 === 'string') {
        const val = answers.phase_10;
        const isCardiac = val.toLowerCase().includes('cardiaque');
        const c = '<span style="display:inline-block;padding:4px 14px;border-radius:6px;font-size:13px;font-weight:600;'
          + 'background:' + (isCardiac ? '#FEE2E2' : '#FEF3C7') + ';'
          + 'color:' + (isCardiac ? '#DC2626' : '#92400E') + ';">'
          + val + '</span>';
        phasesHtml += buildPhaseBlock(10, 'Distinction Anxiété vs Urgence Cardiaque', c);
      }
    }

    // Red flags
    const rfHtml = rf.length > 0
      ? '<div style="margin:16px 0;padding:12px;border-left:4px solid #DC2626;background:#FEF2F2;border-radius:8px;">'
        + '<h4 style="color:#DC2626;font-size:14px;font-weight:bold;margin-bottom:8px;">Drapeaux rouges détectés (' + rf.length + ')</h4>'
        + '<ul style="list-style:none;padding:0;">' + rf.map(function(f) { return '<li style="font-size:13px;color:#B91C1C;padding:2px 0;">&#9888; ' + f + '</li>'; }).join('') + '</ul></div>'
      : '';

    // Conversation
    const chatHtml = transcript.map(function(m) {
      return '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:' + (m.role === 'user' ? '#EFF6FF' : '#F0FDF4') + ';">'
        + '<strong style="font-size:12px;color:' + (m.role === 'user' ? '#1E40AF' : '#166534') + ';">' + (m.role === 'user' ? 'Répartiteur' : 'Citoyen') + ':</strong> '
        + '<span style="font-size:13px;">' + esc(m.text) + '</span></div>';
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Autorisez les popups'); return; }

    printWindow.document.write('<!DOCTYPE html><html><head><title>Protocole Santé — ' + esc(session.scenario_title || '') + '</title>'
      + '<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Segoe UI,Arial,sans-serif;padding:30px;color:#1e293b;line-height:1.5;max-width:900px;margin:auto;}'
      + '.header{text-align:center;margin-bottom:20px;padding-bottom:12px;border-bottom:2px solid #e2e8f0;}'
      + '.meta{display:flex;gap:20px;flex-wrap:wrap;padding:10px 14px;background:#f8fafc;border-radius:8px;margin-bottom:14px;font-size:13px;}'
      + 'h3{margin:16px 0 8px;font-size:15px;color:#002768;}'
      + '@media print{body{margin:15px;font-size:12px;}}</style></head><body>'
      + '<div class="header"><h1 style="font-size:18px;color:#002768;">Protocole Santé — Douleur Thoracique</h1>'
      + '<p style="font-size:12px;color:#64748b;">Scénario: ' + esc(session.scenario_title || '') + '</p></div>'
      + '<div class="meta"><div><strong>Étudiant:</strong> ' + esc(session.operator_name || '') + '</div>'
      + '<div><strong>Date:</strong> ' + new Date(session.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</div>'
      + '<div><strong>Durée:</strong> ' + formatTime(duration) + '</div></div>'
      + '<div style="padding:12px;border:2px solid ' + p.color + ';background:' + p.bg + ';border-radius:10px;margin-bottom:14px;">'
      + '<h2 style="font-size:16px;color:' + p.color + ';">' + p.label + ' — ' + p.title + '</h2></div>'
      + rfHtml
      + '<h3>Réponses du protocole</h3>'
      + phasesHtml
      + (chatHtml ? '<h3>Conversation avec le citoyen</h3>' + chatHtml : '')
      + '<div style="margin-top:20px;text-align:center;font-size:11px;color:#94a3b8;">Simulateur 911 — ' + new Date().toLocaleDateString('fr-FR') + '</div>'
      + '<script>window.onload=function(){window.print();}</script>'
      + '</body></html>');
    printWindow.document.close();
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session? Cette action est irréversible.')) {
      return;
    }

    setDeletingSessionId(sessionId);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session supprimée avec succès');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingSessionId(null);
    }
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedSessions(new Set());
  };

  // Toggle single session selection
  const toggleSessionSelection = (sessionId) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  // Select all sessions
  const selectAllSessions = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map(s => s.id)));
    }
  };

  // Delete multiple sessions
  const deleteSelectedSessions = async () => {
    if (selectedSessions.size === 0) {
      toast.error('Aucune session sélectionnée');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedSessions.size} session(s)? Cette action est irréversible.`)) {
      return;
    }

    setDeletingMultiple(true);
    let deletedCount = 0;
    let errorCount = 0;

    for (const sessionId of selectedSessions) {
      try {
        await axios.delete(`${API}/sessions/${sessionId}`);
        deletedCount++;
      } catch (error) {
        console.error(`Error deleting session ${sessionId}:`, error);
        errorCount++;
      }
    }

    // Update the sessions list
    setSessions(sessions.filter(s => !selectedSessions.has(s.id)));
    setSelectedSessions(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);

    if (errorCount === 0) {
      toast.success(`${deletedCount} session(s) supprimée(s) avec succès`);
    } else {
      toast.warning(`${deletedCount} supprimée(s), ${errorCount} erreur(s)`);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'low': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      critical: 'Critique',
      high: 'Élevé', 
      medium: 'Moyen',
      low: 'Faible'
    };
    return labels[severity] || severity;
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'aborted': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  const getStatusLabel = (status) => {
    const labels = {
      completed: 'Terminé',
      in_progress: 'En cours',
      aborted: 'Abandonné'
    };
    return labels[status] || status;
  };
  
  if (loading) {
    return (
      <div data-testid="history-loading" className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-xl" style={{ color: '#2E6CF5' }}>Chargement...</div>
      </div>
    );
  }
  
  return (
    <div data-testid="history-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#002768' }}>Historique des Sessions</h1>
        <p className="text-lg text-gray-600">
          {isAdmin ? 'Toutes les sessions de formation' : 'Consultez vos sessions d\'entraînement passées'}
        </p>
      </div>

      {/* Admin: Student filter */}
      {isAdmin && sessions.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filtrer par nom d'étudiant..."
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none w-full max-w-sm text-sm"
              style={{ '--tw-ring-color': '#2E6CF5' }}
              data-testid="student-filter-input"
            />
            {studentFilter && (
              <button onClick={() => setStudentFilter('')} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
      
      {sessions.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <p className="text-gray-500 text-lg">Aucune session d'entraînement trouvée.</p>
          <a
            href="/scenarios"
            className="inline-block mt-6 px-6 py-3 text-white rounded-lg font-semibold hover:shadow-lg"
            style={{ background: 'linear-gradient(to right, #002768, #2E6CF5)' }}
          >
            Commencer un Entraînement
          </a>
        </div>
      ) : (
        <>
          {/* Selection Toolbar */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              onClick={toggleSelectionMode}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                isSelectionMode 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={isSelectionMode ? { backgroundColor: '#002768' } : {}}
            >
              {isSelectionMode ? <XSquare className="w-4 h-4" /> : <CheckSquare className="w-4 h-4" />}
              <span>{isSelectionMode ? 'Annuler' : 'Sélectionner'}</span>
            </button>

            {isSelectionMode && (
              <>
                <button
                  onClick={selectAllSessions}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all"
                >
                  <CheckSquare className="w-4 h-4" />
                  <span>
                    {selectedSessions.size === sessions.length ? 'Désélectionner tout' : 'Tout sélectionner'}
                  </span>
                </button>

                <span className="text-gray-500 text-sm">
                  {selectedSessions.size} sélectionnée(s)
                </span>

                {selectedSessions.size > 0 && (
                  <button
                    onClick={deleteSelectedSessions}
                    disabled={deletingMultiple}
                    className="flex items-center space-x-2 px-4 py-2 bg-rose-500 text-white rounded-lg font-medium hover:bg-rose-600 disabled:opacity-50 transition-all ml-auto"
                  >
                    {deletingMultiple ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    <span>Supprimer ({selectedSessions.size})</span>
                  </button>
                )}
              </>
            )}
          </div>

          <div data-testid="sessions-list" className="space-y-4">
            {sessions
              .filter(s => !studentFilter || s.operator_name?.toLowerCase().includes(studentFilter.toLowerCase()))
              .map((session) => (
              <Card 
                key={session.id} 
                data-testid={`session-card-${session.id}`} 
                className={`glass border-0 hover:shadow-xl transition-all ${
                  isSelectionMode && selectedSessions.has(session.id) ? 'ring-2 bg-blue-50' : ''
                }`}
                onClick={isSelectionMode ? () => toggleSessionSelection(session.id) : undefined}
                style={isSelectionMode && selectedSessions.has(session.id) ? { cursor: 'pointer', '--tw-ring-color': '#2E6CF5' } : isSelectionMode ? { cursor: 'pointer' } : {}}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      {isSelectionMode && (
                        <div className="flex-shrink-0">
                          {selectedSessions.has(session.id) ? (
                            <CheckSquare className="w-6 h-6" style={{ color: '#2E6CF5' }} />
                          ) : (
                            <Square className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                      )}
                      <div>
                        <CardTitle className="flex items-center space-x-3">
                          <User className="w-5 h-5" style={{ color: '#2E6CF5' }} />
                          <span>{session.operator_name}</span>
                        </CardTitle>
                        <CardDescription className="flex items-center space-x-2 mt-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(session.start_time).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className={getStatusColor(session.status)}>
                      {getStatusLabel(session.status)}
                    </Badge>
                  </div>
                </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-xs text-gray-500">Durée</div>
                      <div className="font-semibold text-gray-800">{formatDuration(session.duration_seconds)}</div>
                    </div>
                  </div>
                  
                  {session.evaluation_score && (
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-amber-500" />
                      <div>
                        <div className="text-xs text-gray-500">Score</div>
                        <div className="font-semibold text-gray-800">{session.evaluation_score}/10</div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <div className="text-xs text-gray-500">ID Session</div>
                    <div className="font-mono text-xs text-gray-600">{session.id.slice(0, 8)}...</div>
                  </div>

                  {session.scenario_category === 'incendie' && session.hints_count > 0 && (
                    <div className="flex items-center space-x-2">
                      <Lightbulb className="w-4 h-4" style={{ color: '#F59E0B' }} />
                      <div>
                        <div className="text-xs text-gray-500">Indices</div>
                        <div className="font-semibold text-gray-800">{session.hints_count} utilisé{session.hints_count > 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  )}
                </div>
                
                {session.evaluation_notes && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#CAE9FA' }}>
                    <div className="text-xs font-semibold mb-1" style={{ color: '#002768' }}>Notes d'Évaluation</div>
                    <p className="text-sm text-gray-700">{session.evaluation_notes}</p>
                  </div>
                )}

                {/* Action Buttons */}
                {!isSelectionMode && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => viewScenario(session.scenario_id)}
                      className="flex items-center space-x-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                      style={{ backgroundColor: '#2E6CF5' }}
                      data-testid={`view-scenario-${session.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      <span>Voir Scénario</span>
                    </button>
                    
                    <button
                      onClick={() => viewConversation(session)}
                      className="flex items-center space-x-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                      style={{ backgroundColor: '#24B219' }}
                      data-testid={`view-conversation-${session.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Voir Conversation</span>
                    </button>

                    {session.health_protocol && (
                      <button
                        onClick={() => printHealthProtocol(session)}
                        className="flex items-center space-x-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                        style={{ backgroundColor: '#DC2626' }}
                        data-testid={`print-protocol-${session.id}`}
                      >
                        <Heart className="w-4 h-4" />
                        <span>Protocole Santé</span>
                      </button>
                    )}

                    {session.scenario_category === 'ambulance' && session.status === 'completed' && (
                      session.has_auto_evaluation ? (
                        <button
                          onClick={() => navigate(`/auto-evaluation/training/${session.id}`)}
                          className="flex items-center space-x-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                          style={{ backgroundColor: '#7C3AED' }}
                          data-testid={`view-auto-eval-${session.id}`}
                        >
                          <Award className="w-4 h-4" />
                          <span>Voir Évaluation</span>
                        </button>
                      ) : (() => {
                        const reqStatus = evalRequests[`auto_evaluation:${session.id}`];
                        const label = isAdmin ? 'Évaluation Auto'
                          : reqStatus === 'pending' ? 'Évaluation demandée ⏳'
                          : reqStatus === 'refused' ? 'Demande refusée'
                          : 'Demander Évaluation';
                        const color = reqStatus === 'pending' ? '#F59E0B' : reqStatus === 'refused' ? '#EE2130' : '#7C3AED';
                        const bg = reqStatus === 'pending' ? '#FFFBEB' : reqStatus === 'refused' ? '#FEF2F2' : '#F5F3FF';
                        return (
                          <button
                            onClick={() => navigate(`/auto-evaluation/training/${session.id}`)}
                            className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-colors border-2"
                            style={{ borderColor: color, color, backgroundColor: bg }}
                            data-testid={`generate-auto-eval-${session.id}`}
                          >
                            <Award className="w-4 h-4" />
                            <span>{label}</span>
                          </button>
                        );
                      })()
                    )}

                    {session.scenario_category === 'incendie' && session.status === 'completed' && (
                      session.has_retroaction ? (
                        <button
                          onClick={() => navigate(`/retroaction/${session.id}`)}
                          className="flex items-center space-x-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors"
                          style={{ backgroundColor: '#F4621C' }}
                          data-testid={`view-retroaction-${session.id}`}
                        >
                          <Target className="w-4 h-4" />
                          <span>Voir Rétroaction</span>
                        </button>
                      ) : (() => {
                        const reqStatus = evalRequests[`retroaction:${session.id}`];
                        const label = isAdmin ? 'Générer Rétroaction'
                          : reqStatus === 'pending' ? 'Rétroaction demandée ⏳'
                          : reqStatus === 'refused' ? 'Demande refusée'
                          : 'Demander Rétroaction';
                        const color = reqStatus === 'pending' ? '#F59E0B' : reqStatus === 'refused' ? '#EE2130' : '#F4621C';
                        const bg = reqStatus === 'pending' ? '#FFFBEB' : reqStatus === 'refused' ? '#FEF2F2' : '#FFF7ED';
                        return (
                          <button
                            onClick={() => navigate(`/retroaction/${session.id}`)}
                            className="flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-colors border-2"
                            style={{ borderColor: color, color, backgroundColor: bg }}
                            data-testid={`generate-retroaction-${session.id}`}
                          >
                            <Target className="w-4 h-4" />
                            <span>{label}</span>
                          </button>
                        );
                      })()
                    )}
                    
                    <button
                      onClick={() => deleteSession(session.id)}
                      disabled={deletingSessionId === session.id}
                      className="flex items-center space-x-1 px-3 py-2 bg-rose-500 text-white rounded-lg text-sm font-medium hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid={`delete-session-${session.id}`}
                    >
                      {deletingSessionId === session.id ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      <span>Supprimer</span>
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      {/* Conversation Modal */}
      {showConversationModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Transcript de la Conversation</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Opérateur: <span className="font-semibold">{selectedSession.operator_name}</span> • 
                    Durée: <span className="font-semibold">{formatDuration(selectedSession.duration_seconds || 0)}</span> •
                    Messages: <span className="font-semibold">{selectedSession.transcript?.length || 0}</span>
                  </p>
                  
                  {/* Scenario Viewing Information */}
                  <div className="mt-3">
                    {selectedSession.scenario_viewed ? (
                      <div className="inline-flex items-center space-x-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs">
                        <Eye className="w-3 h-3 text-amber-600" />
                        <span className="text-amber-700 font-medium">
                          Scénario consulté {selectedSession.scenario_view_count || 1} fois
                        </span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center space-x-2 px-3 py-1 border rounded-full text-xs" style={{ backgroundColor: '#E0F8E0', borderColor: '#24B219' }}>
                        <EyeOff className="w-3 h-3" style={{ color: '#24B219' }} />
                        <span className="font-medium" style={{ color: '#24B219' }}>
                          Scénario non consulté - Travail autonome
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowConversationModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center ml-4"
                  data-testid="close-conversation-modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {selectedSession.transcript && selectedSession.transcript.length > 0 ? (
                <div className="space-y-4">
                  {selectedSession.transcript.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.speaker === 'operator' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`flex items-start space-x-3 max-w-[75%] ${
                          message.speaker === 'operator' ? 'flex-row-reverse space-x-reverse' : ''
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white`}
                             style={{ backgroundColor: message.speaker === 'operator' ? '#002768' : '#EE2130' }}>
                          {message.speaker === 'operator' ? (
                            <User className="w-5 h-5" />
                          ) : (
                            <Bot className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className={`px-4 py-3 rounded-2xl ${
                            message.speaker === 'operator'
                              ? 'text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-800 rounded-bl-md'
                          }`} style={message.speaker === 'operator' ? { backgroundColor: '#002768' } : {}}>
                            <p className="text-sm leading-relaxed">{message.text}</p>
                          </div>
                          <div className={`text-xs text-gray-500 mt-1 ${
                            message.speaker === 'operator' ? 'text-right' : 'text-left'
                          }`}>
                            <span className="font-semibold capitalize">
                              {message.speaker === 'operator' ? 'Opérateur' : 'Citoyen'}
                            </span>
                            {message.timestamp && (
                              <span className="ml-2">{formatTimestamp(message.timestamp)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucune conversation enregistrée pour cette session.</p>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={printConversation}
                  className="flex-1 flex items-center justify-center space-x-2 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#24B219' }}
                  data-testid="print-conversation-button"
                >
                  <Printer className="w-5 h-5" />
                  <span>Imprimer</span>
                </button>
                <button
                  onClick={() => setShowConversationModal(false)}
                  className="flex-1 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#002768' }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Details Modal */}
      {showScenarioModal && selectedScenario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Détails du Scénario</h2>
                <button
                  onClick={() => setShowScenarioModal(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                  data-testid="close-scenario-modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold" style={{ color: '#002768' }}>{selectedScenario.title}</h3>
                  <Badge className={getSeverityColor(selectedScenario.severity)}>
                    {getSeverityLabel(selectedScenario.severity)}
                  </Badge>
                </div>
                
                <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                  <span className="font-semibold">Catégorie:</span> {selectedScenario.category?.toUpperCase()}
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Situation</h4>
                  <p className="text-gray-600 leading-relaxed">{selectedScenario.description}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Localisation</h4>
                  <p className="text-gray-600">{selectedScenario.location}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Informations Victime</h4>
                  <p className="text-gray-600">{selectedScenario.victim_info}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Instructions pour les Secours</h4>
                  <p className="text-gray-600">{selectedScenario.instructions}</p>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowScenarioModal(false)}
                  className="w-full py-3 text-white rounded-lg font-semibold hover:opacity-90"
                  style={{ backgroundColor: '#002768' }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;